'use client';

/**
 * ToastContext - Unified Toast System
 * 
 * Use this toast system (`useToast`) for consistent app-wide notifications.
 * Example:
 *   const { addToast } = useToast();
 *   addToast('Your message', 'success'); // also supports 'error', 'warning', 'info'
 * 
 * Avoid direct use of react-hot-toast; always use this for styling and behavior consistency.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X, AlertTriangle, Info, XCircle } from 'lucide-react';
import { eventBus } from '../lib/event-bus';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  addToast: (message: string, type: ToastType) => void;
  removeToast: (id: string) => void;
  toasts: Toast[];
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const TOAST_DURATIONS = {
  success: 3000,
  error: 4000,
  warning: 4000,
  info: 4000,
};

const TOAST_COLORS = {
  success: {
    bg: '#10b981',
    icon: '#fff',
  },
  error: {
    bg: '#ef4444',
    icon: '#fff',
  },
  warning: {
    bg: '#eab308',
    icon: '#fff',
  },
  info: {
    bg: '#3b82f6',
    icon: '#fff',
  },
};

const ToastIcon = ({ type }: { type: ToastType }) => {
  const iconClass = 'w-5 h-5 flex-shrink-0';
  
  switch (type) {
    case 'success':
      return <CheckCircle2 className={iconClass} strokeWidth={2.5} />;
    case 'error':
      return <XCircle className={iconClass} strokeWidth={2.5} />;
    case 'warning':
      return <AlertTriangle className={iconClass} strokeWidth={2.5} />;
    case 'info':
      return <Info className={iconClass} strokeWidth={2.5} />;
    default:
      return null;
  } 
};

const ToastItem = ({
  toast,
  onRemove,
}: {
  toast: Toast;
  onRemove: (id: string) => void;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Trigger entrance animation (matching react-hot-toast timing)
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    // Auto-remove after duration
    const duration = TOAST_DURATIONS[toast.type];
    timerRef.current = setTimeout(() => {
      handleRemove();
    }, duration);

    return () => {
      clearTimeout(timer);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 250); // Match react-hot-toast exit animation duration
  };

  const colors = TOAST_COLORS[toast.type];

  return (
    <div
      className={`
        text-white rounded-lg
        w-full max-w-full sm:min-w-[300px] sm:max-w-[500px]
        px-4 py-3 mb-2
        flex items-center gap-3
        shadow-[0_4px_12px_rgba(0,0,0,0.15)]
        transform transition-all
        ${isVisible && !isRemoving 
          ? 'opacity-100 translate-x-0 scale-100' 
          : 'opacity-0 translate-x-[110%] scale-[0.8]'
        }
      `}
      style={{
        backgroundColor: colors.bg,
        transitionDuration: '250ms',
        transitionTimingFunction: 'cubic-bezier(0.21, 1.02, 0.73, 1)',
        willChange: 'transform, opacity',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 500,
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <div className="flex-shrink-0" style={{ color: colors.icon }}>
        <ToastIcon type={toast.type} />
      </div>

      {/* Message */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-5 break-words">
          {toast.message}
        </p>
      </div>

      {/* Close Button */}
      <button
        onClick={handleRemove}
        className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors opacity-70 hover:opacity-100"
        aria-label="Close toast"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || toasts.length === 0) return null;

  return createPortal(
    <div 
      className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 z-[99999] pointer-events-none"
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 99999,
      }}
    >
      <div className="pointer-events-auto">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
        ))}
      </div>
    </div>,
    document.body
  );
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType) => {
      // If message is falsy, do not add toast
      if (!message) {
        return;
      }

      counterRef.current += 1;
      const id = `toast-${Date.now()}-${counterRef.current}`;

      setToasts((prev) => {
        // Limit to 5 toasts max
        const newToasts = [...prev, { id, message, type }];
        return newToasts.slice(-5);
      });
    },
    []
  );

  useEffect(() => {
    const unsubscribe = eventBus.on('SHOW_TOAST', (data) => {
      addToast(data.message, data.type);
    });

    return () => {
      unsubscribe();
    };
  }, [addToast]);

  const contextValue = useMemo(
    () => ({ addToast, removeToast, toasts }),
    [addToast, removeToast, toasts],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
